import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  countPaymentProofs,
  deletePaymentProof,
  listPaymentProofs,
  uploadPaymentProof,
  type PaymentProof,
  type UploadProofInput,
} from '@/lib/api/paymentProofs';

const KEYS = {
  all: ['payment-proofs'] as const,
  byTransaction: (id: string) => [...KEYS.all, 'tx', id] as const,
  counts: (ids: string[]) => [...KEYS.all, 'counts', ids.join(',')] as const,
};

export const usePaymentProofs = (transactionId: string | null) =>
  useQuery({
    queryKey: KEYS.byTransaction(transactionId ?? ''),
    queryFn: () => (transactionId ? listPaymentProofs(transactionId) : Promise.resolve([])),
    enabled: !!transactionId,
    // Signed URLs expire after an hour; refetch well inside that.
    staleTime: 20 * 60 * 1000,
  });

export const usePaymentProofCounts = (transactionIds: string[]) =>
  useQuery({
    queryKey: KEYS.counts(transactionIds),
    queryFn: () => countPaymentProofs(transactionIds),
    enabled: transactionIds.length > 0,
    staleTime: 60 * 1000,
  });

export const useUploadPaymentProof = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadProofInput) => uploadPaymentProof(input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.byTransaction(vars.transactionId) });
      qc.invalidateQueries({ queryKey: [...KEYS.all, 'counts'] });
    },
  });
};

export const useDeletePaymentProof = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (proof: PaymentProof) => deletePaymentProof(proof),
    onSuccess: (_, proof) => {
      qc.invalidateQueries({ queryKey: KEYS.byTransaction(proof.transactionId) });
      qc.invalidateQueries({ queryKey: [...KEYS.all, 'counts'] });
    },
  });
};
